import { useMemo, useCallback } from "react";
import { createListCollection } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { useDashboardStoreActions, useSelectedTimeRange } from "@/features/dashboard/store";
import { useDashboardData } from "@/features/shared/hooks";
import Select, { SelectOption } from "@/components/select/select";

export default function TimeRangeSelector() {
  const { t } = useTranslation();
  const { hoursStart, hoursEnd } = useSelectedTimeRange();
  const { selectedDashboard } = useDashboardData();
  const { setTimeRange } = useDashboardStoreActions();

  const [startTimes, endTimes] = useMemo<[SelectOption[], SelectOption[]]>(() => {
    if (!selectedDashboard) return [[], []];

    const mapTime = (duration: number): SelectOption => {
      const hours = duration;
      const timePeriod = hours % 24 === 0 ? "days" : "hours";

      let label = t("common.now");
      if (timePeriod === "hours" && hours !== 0) {
        label = t("common.hours", { hours });
      }
      if (timePeriod === "days" && hours !== 0) {
        const days = hours / 24;
        if (days === 1) {
          label = t("common.days_singular", { days });
        } else {
          label = t("common.days", { days });
        }
      }

      return {
        label,
        value: duration.toString(),
      };
    };

    const timeRangeSelectorSettings = selectedDashboard.time_range_selector_settings;
    const startOptions = timeRangeSelectorSettings?.start_options?.map(mapTime) ?? [];

    const endOptions = timeRangeSelectorSettings?.end_options?.map(mapTime) ?? [];

    return [startOptions, endOptions];
  }, [selectedDashboard, t]);

  const handleSetTime = useCallback(
    (timeRange: { startHour?: number; endHour?: number }) => {
      if (!selectedDashboard) return;

      setTimeRange(timeRange);
    },
    [selectedDashboard, setTimeRange],
  );

  const startCollection = createListCollection({
    items: startTimes,
  });
  const endCollection = createListCollection({
    items: endTimes,
  });

  return (
    <div style={{ display: "flex", gap: "1rem" }}>
      <Select
        label={t("common.from")}
        collection={startCollection}
        value={hoursStart?.toString()}
        onValueChange={(value) => handleSetTime({ startHour: Number(value) })}
        placeholder={t("common.from")}
      />
      <Select
        label={t("common.to")}
        collection={endCollection}
        value={hoursEnd?.toString()}
        onValueChange={(value) => handleSetTime({ endHour: Number(value) })}
        placeholder={t("common.to")}
      />
    </div>
  );
}
